import CenterFocusWeakIcon from "@mui/icons-material/CenterFocusWeak";
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import {
  CopyLinkButton,
  MANUAL_REVIEW_LINK_TARGET,
} from "components/labelling/CopyLinkButton";
import { AllAnglesVideoPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/AllAnglesVideoPlayer";
import { PlaybackControls } from "components/scrapEyeDataDashboard/VideoPlayer/components/PlaybackControls";
import { VideoTimeline } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoTimeline";
import { HotkeyProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import {
  VideoControlProvider,
  useVideoControl,
  useVideoControlActions,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import type { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useEffect, useState } from "react";
import {
  PLAYBACK_SCOPE,
  SKIP_BUTTON_SECONDS,
} from "components/scrapEyeDataDashboard/VideoPlayer/constants";
import { useSkipAndStepHandlers } from "components/scrapEyeDataDashboard/VideoPlayer/hooks/useSkipAndStepHandlers";

const VIDEO_GRID_LAYOUT = {
  minTileWidth: "260px",
  maxTileHeight: "65vh",
} as const;

interface VideoPanelProps {
  windowStart: Dayjs;
  windowEnd: Dayjs;
  onRecenter: () => void;
  onVideoTimeChange: (timestamp: Date) => void;
  storageKey?: string;
}

export const VideoPanel = (props: VideoPanelProps) => (
  <HotkeyProvider>
    <VideoControlProvider>
      <VideoPanelInner {...props} />
    </VideoControlProvider>
  </HotkeyProvider>
);

const VideoPanelInner = ({
  windowStart,
  windowEnd,
  onRecenter,
  onVideoTimeChange,
  storageKey = "bucketAssignmentTool",
}: VideoPanelProps) => {
  const { t } = useTenantTranslation();
  const { state } = useVideoControl();
  const actions = useVideoControlActions();

  const { locationNames } = useLabellingLocations();

  const [selectedLocation, setSelectedLocation] = useState<string | null>(
    () => locationNames[0] ?? null
  );

  const effectiveLocation = selectedLocation ?? locationNames[0] ?? null;

  useEffect(() => {
    const durationSeconds =
      (windowEnd.valueOf() - windowStart.valueOf()) / 1000;
    actions.reset();
    actions.updateDuration(durationSeconds);
    actions.seekTo(0);
  }, [windowStart, windowEnd, actions]);

  useEffect(() => {
    onVideoTimeChange(windowStart.add(state.currentTime, "second").toDate());
  }, [state.currentTime, windowStart, onVideoTimeChange]);

  const {
    handleSkipBackward,
    handleSkipForward,
    handleStepBackward,
    handleStepForward,
  } = useSkipAndStepHandlers({ skipSeconds: SKIP_BUTTON_SECONDS });

  return (
    <Box>
      {locationNames.length > 1 ? (
        <Stack direction="row" sx={{ mb: 1 }}>
          <ToggleButtonGroup
            value={effectiveLocation}
            exclusive
            onChange={(_, v: string | null) => {
              if (v !== null) setSelectedLocation(v);
            }}
            size="small"
          >
            {locationNames.map((name) => (
              <ToggleButton key={name} value={name}>
                {name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      ) : null}
      {effectiveLocation !== null ? (
        <AllAnglesVideoPlayer
          hotkeyScope={PLAYBACK_SCOPE}
          key={`${effectiveLocation}-${windowStart.toISOString()}`}
          location={effectiveLocation}
          startIso={windowStart.toISOString()}
          endIso={windowEnd.toISOString()}
          videoStart={windowStart}
          showVideoControls={false}
          gridLayout={VIDEO_GRID_LAYOUT}
          storageKey={storageKey}
        />
      ) : null}
      <Stack spacing={1} sx={{ mt: 2, alignItems: "center" }}>
        <Box sx={{ width: "100%" }}>
          <VideoTimeline
            currentTime={state.currentTime}
            duration={state.duration}
            onSeek={actions.seekTo}
            videoStart={windowStart}
            disableZoom
          />
        </Box>
        <PlaybackControls
          isPlaying={state.isPlaying}
          playbackRate={state.playbackRate}
          onTogglePlayPause={actions.togglePlayPause}
          onSkipBackward={handleSkipBackward}
          onSkipForward={handleSkipForward}
          onSpeedChange={actions.setPlaybackRate}
          videoStart={windowStart}
          onStepBackward={handleStepBackward}
          onStepForward={handleStepForward}
          actionButtons={
            <Stack direction="row" alignItems="center">
              <Tooltip title={t("recenterVideoOnVisibleRange")}>
                <IconButton size="small" onClick={onRecenter}>
                  <CenterFocusWeakIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {effectiveLocation !== null ? (
                <CopyLinkButton
                  location={effectiveLocation}
                  videoStart={windowStart}
                  linkTarget={MANUAL_REVIEW_LINK_TARGET}
                  iconFontSize="small"
                />
              ) : null}
            </Stack>
          }
        />
      </Stack>
    </Box>
  );
};
